'use client';

import ServiceCard from '@/components/features/services/ServiceCard';
import ServiceFilters from '@/components/features/services/ServiceFilters';
import { EmptyState, ErrorMessage, LoadingSpinner, SectionHeader } from '@/components/ui';
import { mockServices, mockCategories } from '@/mocks/data/services';
import { cssVars } from '@/styles/theme';
import { Briefcase, Search } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';

export default function BrowseServicesPage() {
  const t = useTranslations('pages.services');
  const locale = useLocale();
  const isRTL = locale === 'ar';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 10000 });
  const [maxDeliveryTime, setMaxDeliveryTime] = useState(30);
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState<'popular' | 'price-low' | 'price-high' | 'rating'>(
    'popular'
  );

  // Simulated loading state
  const [isLoading] = useState(false);
  const [error] = useState<Error | null>(null);

  const filteredServices = useMemo(() => {
    let filtered = mockServices;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (service) =>
          service.title.toLowerCase().includes(query) ||
          service.description.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategories.length > 0) {
      filtered = filtered.filter((service) => selectedCategories.includes(service.categoryId));
    }

    // Price filter
    filtered = filtered.filter(
      (service) => service.price >= priceRange.min && service.price <= priceRange.max
    );

    // Delivery time filter
    filtered = filtered.filter((service) => service.deliveryTime <= maxDeliveryTime);

    // Rating filter
    if (minRating > 0) {
      filtered = filtered.filter((service) => (service.rating || 0) >= minRating);
    }

    // Sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      default:
        filtered.sort((a, b) => (b.reviewsCount || 0) - (a.reviewsCount || 0));
    }

    return filtered;
  }, [searchQuery, selectedCategories, priceRange, maxDeliveryTime, minRating, sortBy]);

  return (
    <div
      className="relative min-h-screen py-20"
      style={{
        backgroundColor: cssVars.neutral.bg,
      }}
    >
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute start-0 top-1/4 h-96 w-96 rounded-full blur-3xl"
          style={{ background: cssVars.accent.primary }}
        />
        <div
          className="absolute bottom-1/4 end-0 h-96 w-96 rounded-full blur-3xl"
          style={{ background: cssVars.primary.DEFAULT }}
        />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <SectionHeader
            badge={t('badge')}
            title={t('title')}
            subtitle={t('subtitle')}
            variant="light"
            align={isRTL ? 'right' : 'left'}
            badgeColor="warm"
            className="max-w-3xl"
          />
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Search
              className="absolute start-4 top-1/2 h-5 w-5 -translate-y-1/2"
              style={{ color: cssVars.neutral.textSecondary }}
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full rounded-2xl border-2 py-4 pe-4 ps-12 text-lg transition-all focus:outline-none focus:ring-2"
              style={{
                backgroundColor: cssVars.neutral.surface,
                borderColor: cssVars.neutral.border,
                color: cssVars.neutral.text,
              }}
            />
          </div>
        </div>

        {/* Sort & Results Count */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm" style={{ color: cssVars.neutral.textSecondary }}>
            {t('showing')} <span className="font-bold">{filteredServices.length}</span>{' '}
            {t('services')}
          </div>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="rounded-lg border px-4 py-2 text-sm"
            style={{
              backgroundColor: cssVars.neutral.surface,
              borderColor: cssVars.neutral.border,
              color: cssVars.neutral.text,
            }}
          >
            <option value="popular">{t('sortBy.popular')}</option>
            <option value="price-low">{t('sortBy.priceLow')}</option>
            <option value="price-high">{t('sortBy.priceHigh')}</option>
            <option value="rating">{t('sortBy.rating')}</option>
          </select>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        )}

        {error && !isLoading && (
          <ErrorMessage
            error={error.message || t('error') || 'Failed to load services'}
            variant="banner"
          />
        )}

        {!isLoading && !error && (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
            {/* Filters Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24">
                <ServiceFilters
                  categories={mockCategories}
                  onCategoryChange={setSelectedCategories}
                  onPriceRangeChange={(min, max) => setPriceRange({ min, max })}
                  onDeliveryTimeChange={setMaxDeliveryTime}
                  onRatingChange={setMinRating}
                  isMockData={true}
                />
              </div>
            </aside>

            {/* Services Grid */}
            <main className="lg:col-span-3">
              {filteredServices.length === 0 ? (
                <EmptyState
                  icon={Briefcase}
                  title={t('noServices')}
                  description={
                    searchQuery || selectedCategories.length > 0 ? t('noResults') : t('emptyState')
                  }
                />
              ) : (
                <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                  {filteredServices.map((service, index) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      index={index}
                      isMockData={true}
                    />
                  ))}
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
